// The connection's honesty panel (Mac MacDiagnosticsView, PARITY P319): conversation header → the
// pulse icon. Every number a stuck screen needs to explain itself — socket state and its last sign
// of life, the open conversation in the store, the last open's timing, a live store-read probe — plus
// the one button that tears it all down and refetches («Forzar reconexión y recarga»).
// Built the day theory ran out (2026-09-04): "messages not arriving" → this says WHICH link lies.
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import type { ConversationDiagnostics } from '../../../data/features/extras';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Dialog } from '../../kit/Kit';
import { lastPerf } from '../../perf';
import './extras.css';

function ago(atMillis: number, now: number): string {
  if (!atMillis) return t('web.extras.diag.never');
  const seconds = Math.floor((now - atMillis) / 1000);
  if (seconds < 2) return t('web.extras.diag.now');
  if (seconds < 120) return tf('web.extras.diag.secondsAgo', seconds);
  return tf('web.extras.diag.minutesAgo', Math.floor(seconds / 60));
}

function socketLabel(state: string): string {
  switch (state) {
    case 'connected':
    case 'connecting':
    case 'authenticating':
    case 'reconnecting':
    case 'disconnected':
      return t(`web.extras.diag.ws.${state}`);
    default:
      return state;
  }
}

export function DiagnosticsButton({ channelId }: { channelId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="icon-button diagnostics-button" onClick={() => setOpen(true)} title={t('mac.diagnostics')} aria-label={t('mac.diagnostics')}>
        <Icon name="activity" size={17} />
      </button>
      {/* A portal: the glass header's backdrop-filter is a containing block for `position: fixed`. */}
      {open ? createPortal(<DiagnosticsPanel channelId={channelId} onClose={() => setOpen(false)} />, document.body) : null}
    </>
  );
}

function DiagnosticsPanel({ channelId, onClose }: { channelId: string; onClose(): void }) {
  const [d, setD] = useState<ConversationDiagnostics | null>(null);
  const [storage, setStorage] = useState<string>('');
  const [now, setNow] = useState(() => Date.now());
  const [resyncing, setResyncing] = useState(false);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let live = true;
    const read = () => {
      void data.call('conversationDiagnostics', channelId).then((v) => live && setD(v)).catch(() => undefined);
      setNow(Date.now());
    };
    read();
    void data.call('diagnostics').then((v) => live && setStorage(String(v.storage ?? ''))).catch(() => undefined);
    const id = setInterval(read, 1000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [channelId, generation]);

  const open = lastPerf().find((e) => e.budget === 'channelSwitch');
  const row = (label: string, value: ReactNode, key?: string) => (
    <div className="diag-row" data-diag={key}>
      <span>{label}</span>
      <span className="diag-value">{value}</span>
    </div>
  );
  return (
    <Dialog
      title={t('mac.diagnostics')}
      onClose={onClose}
      width={420}
      footer={
        <button
          className="primary-button diag-resync"
          disabled={resyncing}
          onClick={() => {
            setResyncing(true);
            void data
              .call('forceResync', channelId)
              .catch(() => false)
              .finally(() => {
                setResyncing(false);
                setGeneration((g) => g + 1);
              });
          }}
        >
          {resyncing ? t('web.extras.diag.resyncing') : t('web.extras.diag.resync')}
        </button>
      }
    >
      <div className="diag-panel">
        <section className="diag-group">
          <h3>{t('web.extras.diag.live')}</h3>
          {row(t('web.extras.diag.state'), d ? socketLabel(d.socket.state) : '…', 'state')}
          {row(t('web.extras.diag.lastEvent'), d ? ago(d.socket.lastEventAt, now) : '…', 'last-event')}
          {row(t('web.extras.diag.events'), d ? String(d.socket.eventCount) : '…', 'events')}
        </section>
        <section className="diag-group">
          <h3>{t('web.extras.diag.conversation')}</h3>
          {row(t('web.extras.diag.cached'), d ? String(d.cachedPosts) : '…', 'cached')}
          {row(t('web.extras.diag.lastMessage'), d ? ago(d.lastPostAt, now) : '…', 'last-message')}
          {row(t('web.extras.diag.reach'), d ? (d.historyReach === 'start' ? t('web.extras.diag.reachStart') : t('web.extras.diag.reachMore')) : '…', 'reach')}
        </section>
        <section className="diag-group">
          <h3>{t('web.extras.diag.lastOpen')}</h3>
          {open ? row(t('web.extras.diag.openMs'), `${open.ms} ms · ${open.detail}`, 'open') : row(t('web.extras.diag.openMs'), t('web.extras.diag.noOpen'), 'open')}
        </section>
        <section className="diag-group">
          <h3>{t('web.extras.diag.database')}</h3>
          {row(t('web.extras.diag.storage'), storage === 'opfs' ? t('web.extras.diag.storageOpfs') : storage === 'memory' ? t('web.extras.diag.storageMemory') : '…', 'storage')}
          {row(t('web.extras.diag.probe'), d ? (d.probeMs == null ? t('web.extras.diag.failed') : `${Math.round(d.probeMs)} ms`) : '…', 'probe')}
          {row(t('web.extras.diag.listReady'), d?.listReadyMs ? `${d.listReadyMs} ms` : '…', 'list-ready')}
          {row(t('web.extras.diag.lastSync'), d ? ago(Number(d.lastFullSyncAt) || 0, now) : '…', 'last-sync')}
          {row(t('web.extras.diag.outbox'), d ? `${d.outboxPending} / ${d.outboxFailed}` : '…', 'outbox')}
        </section>
      </div>
    </Dialog>
  );
}
